import { useEffect, useRef, useState } from 'react';

import { useAssignmentNotifications } from 'src/hooks/use-assignment-notifications.hook';
import {
  NormalizedRealtimePayload,
  RealtimePayload,
} from 'src/interfaces/api.types';
import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import { Class } from 'src/interfaces/class.types';
import { UserRoles } from 'src/interfaces/user-role.enum';
import authService from 'src/services/api/auth.service';
import realtimeService from 'src/services/realtime.service';
import {
  addScheduledAssignmentsEvent,
  removeScheduledAssignmentsEvent,
  updateScheduledAssignmentsEvent,
  useActiveAssignmentsViewSelector,
  useActiveUserClassesSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { normalizeAssignmentEvent } from 'src/utils/assignment-events.utils';
import { isActivityDisplayed } from 'src/utils/assignments.utils';
import { dayjs } from 'src/utils/dayjs-config.utils';

import { useActiveAssignments } from './use-active-assignments';

export const useAssignmentEvents = () => {
  const userRole = authService.getRole();
  const isStudent = userRole === UserRoles.STUDENT;

  const { showNewAssignmentsNotification } = useAssignmentNotifications();
  const selectedClass = useSelectedClassSelector();

  const dispatch = useAppDispatch();

  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const isToDoActive = activeAssignmentsView === AssignmentsViewOptions.TODO;

  const { refetch: refetchAssignments, assignmentsStatus } =
    useActiveAssignments();

  const activeUserClasses = useActiveUserClassesSelector();

  const [currentClasses, setCurrentClasses] = useState<Class[]>();
  const currentClassesIds = currentClasses?.map((cl) => cl.sourcedId) ?? [];

  const isAssignmentQueryPendingRef = useRef(false);

  const [unhandledAssignmentUpdates, setUnhandledAssignmentUpdates] = useState<
    number[]
  >([]);

  const [shouldRefetchToDoAssignments, setShouldRefetchToDoAssignments] =
    useState(false);

  useEffect(() => {
    isAssignmentQueryPendingRef.current = assignmentsStatus === 'pending';
  }, [assignmentsStatus]);

  // update assignments in response to realtime event
  // only when assignment updates triggered by previous realtime event have been completed
  useEffect(() => {
    if (assignmentsStatus === 'pending') return;

    if (!unhandledAssignmentUpdates.length) return;

    refetchAssignments();

    setUnhandledAssignmentUpdates((prevState) => {
      return prevState.slice(1);
    });
    // Avoid including the re-fetching method in the deps as it's redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentsStatus, unhandledAssignmentUpdates]);

  const callbackWrapper = (
    callback: (payload: NormalizedRealtimePayload) => void,
  ) => {
    return (_: string, payload: RealtimePayload) => {
      callback(normalizeAssignmentEvent(payload, currentClassesIds));
    };
  };

  const areActivitiesDisplayed = async (
    activitiesBks: NormalizedRealtimePayload['activitiesBks'],
    hasLessonStarted?: boolean,
  ) => {
    if (!isToDoActive && hasLessonStarted) {
      return true;
    }

    const { data: updatedAssignments } = await refetchAssignments();

    if (!updatedAssignments || !activitiesBks.length) return;

    const displayedAssignments = updatedAssignments.assignments;

    // if at least one displayed, then all the items are displayed since they have common startDate
    return isActivityDisplayed(displayedAssignments, activitiesBks[0]);
  };

  const handleNewAssignments = callbackWrapper(
    async (payload: NormalizedRealtimePayload) => {
      const currentDate = new Date();
      const startDate = payload.startDate && dayjs(payload.startDate);
      const isScheduled = startDate && startDate.diff(currentDate) > 0;

      if (isScheduled) {
        dispatch(addScheduledAssignmentsEvent(payload));

        // early return to prevent assignments re-fetch when scheduled lesson is being assigned
        return;
      }

      const areNewActivitiesDisplayed = await areActivitiesDisplayed(
        payload.activitiesBks,
        !isScheduled,
      );

      if (areNewActivitiesDisplayed && payload.newActivitiesCount) {
        // show notification only to students
        if (isStudent) {
          showNewAssignmentsNotification(payload.newActivitiesCount);
        }

        // refetch assignments regardless role
        if (!isToDoActive) {
          setShouldRefetchToDoAssignments(true);
        }
      }
    },
  );

  const handleAssignmentsUpdates = callbackWrapper(
    async (payload: NormalizedRealtimePayload) => {
      const currentDate = new Date();

      const isAssignmentsQueryPending = isAssignmentQueryPendingRef.current;

      if (isAssignmentsQueryPending) {
        setUnhandledAssignmentUpdates((prevState) => {
          return prevState.concat(currentDate.getTime());
        });
      }

      // deleted assignments
      if (payload.isDeleted) {
        dispatch(removeScheduledAssignmentsEvent(payload));
        refetchAssignments();

        if (!isToDoActive) {
          setShouldRefetchToDoAssignments(true);
        }

        return;
      }
      const areNewActivitiesDisplayed = await areActivitiesDisplayed(
        payload.activitiesBks,
      );
      // out of date assignments
      if (isStudent && areNewActivitiesDisplayed) {
        dispatch(removeScheduledAssignmentsEvent(payload));
        if (payload.newActivitiesCount) {
          showNewAssignmentsNotification(payload.newActivitiesCount);
          return;
        }
      }
      const startDate = payload.startDate && dayjs(payload.startDate);
      const isScheduled = startDate && startDate.diff(currentDate) > 0;

      if (isScheduled) {
        dispatch(updateScheduledAssignmentsEvent(payload));
      }
    },
  );

  const subscribeToAssignments = () =>
    realtimeService.subscribeToAssignments(
      handleNewAssignments,
      handleAssignmentsUpdates,
      currentClasses,
    );

  // Defining current classes
  useEffect(() => {
    if (!activeUserClasses) return;

    if (isStudent || (selectedClass && selectedClass.length > 1)) {
      setCurrentClasses(activeUserClasses);
      return;
    }

    // handle case when specific class selected
    if (selectedClass?.length) {
      setCurrentClasses((prevCurrentClasses) => {
        // keep existing currentClasses to avoid re-subscribing
        // when currently selected class option is clicked again
        if (
          prevCurrentClasses &&
          prevCurrentClasses.length === 1 &&
          prevCurrentClasses[0]?.sourcedId === selectedClass[0].sourcedId
        ) {
          return prevCurrentClasses;
        }

        return [selectedClass[0]];
      });
    }
  }, [isStudent, selectedClass, activeUserClasses]);

  // Handles notification from realtime service
  useEffect(() => {
    if (!currentClasses) return;

    subscribeToAssignments();

    return () => realtimeService.unsubscribeFromAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClasses, isToDoActive]);

  useEffect(() => {
    if (!shouldRefetchToDoAssignments || !isToDoActive) return;

    refetchAssignments();
    setShouldRefetchToDoAssignments(false);
    // Avoid including the re-fetching method in the deps as it's redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetchToDoAssignments, isToDoActive]);
};
